import React, { useState, useMemo, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import ScreenerResultsTable from '../ScreenerResultsTable';
import MobileResultsView from './MobileResultsView';
import ElasticityHeatmapView from './ElasticityHeatmapView';
import { calculateDaysUntilExpiration } from '../../utils/screenerDataHelpers';

/**
 * Responsive router component for screener results
 * Displays table view on desktop, stories view on mobile
 * Desktop users can toggle between views
 *
 * Shared state (favorites, removed rows, filters, days to exit) is lifted here
 * so it persists when switching between Table View and Elasticity Heatmap.
 */
const ScreenerResponsive = ({ results, scenarioParams }) => {
  // Width-based mobile detection misses wide-landscape phones (e.g. iPhone 14
  // Pro = 852×393), so combine with an orientation+short-height check that
  // mirrors the existing FloatingActionPanel.css landscape predicate.
  const isCompactWidth = useIsMobile(768);
  const isLandscapePhone = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isCompactWidth || isLandscapePhone;
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'table', 'stories', 'heatmap'

  // --- Default days from the originally queued results ---
  const defaultDays = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) return 45;
    return results[0]['Days to Gain'] || 45;
  }, [results]);

  // --- Shared state across Table View and Heatmap View ---
  const [favoritedRows, setFavoritedRows] = useState(new Set());
  const [removedRows, setRemovedRows] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [selectedStrikePositions, setSelectedStrikePositions] = useState([]);
  const [sharedDaysToExit, setSharedDaysToExit] = useState(null); // null = per-contract mode

  // --- Days to Exit mode: 'per-contract' | 'fixed' | 'percent' ---
  const [daysToExitMode, setDaysToExitMode] = useState('per-contract');
  const [daysToExitPercent, setDaysToExitPercent] = useState(null); // 0-1 float

  // --- Derive defaults from the last-run scenario queue ---
  const scenarioDefaults = useMemo(() => {
    if (!scenarioParams || scenarioParams.length === 0) return null;
    const first = scenarioParams[0];
    return {
      daysToGain: parseInt(first.days_to_gain) || null,
      stockGainPct: first.stock_gain_pct != null ? Math.round(parseFloat(first.stock_gain_pct) * 100) : null,
      exitPercent: first._exitPercent || null,
    };
  }, [scenarioParams]);

  // Seed Days-to-Exit settings whenever new results arrive: default to Fixed Days mode
  // at 50% of the shortest expiration in the result set. The user can override via
  // the Simulation Settings / Heatmap settings panels after the fact.
  useEffect(() => {
    if (!Array.isArray(results) || results.length === 0) return;

    let shortestDays = Infinity;
    for (const row of results) {
      const exp = row['Expiration'];
      if (!exp) continue;
      const d = calculateDaysUntilExpiration(exp);
      if (d > 0 && d < shortestDays) shortestDays = d;
    }

    if (shortestDays === Infinity) return;
    const halfDays = Math.max(1, Math.round(shortestDays * 0.5));

    setDaysToExitMode('fixed');
    setSharedDaysToExit(halfDays);
    setDaysToExitPercent(null);
  }, [results]);

  // Default to the Elasticity Heatmap view every time a new screener run
  // produces results. If the user manually switches to Detail or Stories
  // mid-session, the next run will reset back to Heatmap.
  useEffect(() => {
    if (Array.isArray(results) && results.length > 0) {
      setViewMode('heatmap');
    }
  }, [results]);

  // Determine which view to show: auto -> heatmap on both desktop & mobile.
  // Stories remains reachable on mobile via the toggle bar but is no longer the default.
  const showMobileView = viewMode === 'stories';
  const showHeatmapView = viewMode === 'heatmap' || viewMode === 'auto';
  const showTableView = viewMode === 'table';

  const sharedProps = {
    favoritedRows, setFavoritedRows,
    removedRows, setRemovedRows,
    showFavoritesOnly, setShowFavoritesOnly,
    selectedExpirations, setSelectedExpirations,
    selectedTickers, setSelectedTickers,
    selectedStrikePositions, setSelectedStrikePositions,
    sharedDaysToExit, setSharedDaysToExit,
    defaultDays,
    daysToExitMode, setDaysToExitMode,
    daysToExitPercent, setDaysToExitPercent,
    scenarioDefaults,
  };

  const toggleStyle = (isActive) => ({
    padding: isMobile ? '6px 10px' : '8px 16px',
    borderRadius: '6px',
    border: isActive ? '2px solid #218aff' : '1px solid #333',
    background: isActive ? '#218aff20' : '#0a0a0a',
    color: isActive ? '#ffffff' : '#b0b0b0',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: isMobile ? '12px' : '14px',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    flexShrink: 0
  });

  return (
    <div className="screener-responsive-container">
      {/* View Toggle (Desktop + Mobile) */}
      <div className="view-toggle-container" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: isMobile ? '6px' : '12px',
        marginBottom: isMobile ? '12px' : '20px',
        padding: isMobile ? '8px' : '12px',
        background: '#1a1a1a',
        borderRadius: '8px',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setViewMode('table')}
          className={`view-toggle-btn ${showTableView ? 'active' : ''}`}
          style={toggleStyle(showTableView)}
        >
          {isMobile ? 'Detail' : 'Detail View'}
        </button>
        <button
          onClick={() => setViewMode('stories')}
          className={`view-toggle-btn ${showMobileView ? 'active' : ''}`}
          style={toggleStyle(showMobileView)}
        >
          {isMobile ? 'Stories' : 'Stories View'}
        </button>
        <button
          onClick={() => setViewMode('heatmap')}
          className={`view-toggle-btn ${showHeatmapView ? 'active' : ''}`}
          style={toggleStyle(showHeatmapView)}
        >
          {isMobile ? 'Heatmap' : 'Elasticity Heatmap'}
        </button>
      </div>

      {/* Conditional Rendering. On mobile, both 'table' (Detail) and 'heatmap'
          route into ElasticityHeatmapView with a `mobileVariant` flag — the
          Detail variant renders the per-contract card list (existing layout),
          the heatmap variant renders the new sticky-grid layout. Desktop
          'table' still renders the wide ScreenerResultsTable. */}
      {showMobileView ? (
        <MobileResultsView results={results} />
      ) : showHeatmapView ? (
        <ElasticityHeatmapView results={results} isMobile={isMobile} mobileVariant="heatmap" {...sharedProps} />
      ) : isMobile ? (
        <ElasticityHeatmapView results={results} isMobile={isMobile} mobileVariant="detail" {...sharedProps} />
      ) : (
        <ScreenerResultsTable results={results} {...sharedProps} />
      )}
    </div>
  );
};

export default ScreenerResponsive;
